import React, { Suspense, useCallback, useMemo, useState } from 'react';

import { ErrorBoundary } from 'react-error-boundary';
import { useIntl } from 'react-intl';
import { graphql, useLazyLoadQuery, usePaginationFragment } from 'react-relay';

import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/core/chevron-up';
import { Inline, Stack } from '@atlaskit/primitives/compiled';
import Skeleton from '@atlaskit/skeleton';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { logException } from '@atlassian/conversation-assistant-instrumentation/services/sentry';
import {
	ExperienceFailure,
	ExperienceStart,
	ExperienceSuccess,
} from '@atlassian/conversation-assistant-instrumentation/ui/ExperienceTracker';
import type { Agent } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { type AssistanceServiceInputParam } from '@atlassian/conversation-assistant-service/services/assistance-service/main';
import { ChatGenericError } from '@atlassian/conversation-assistant-ui-components/ui/ChatGenericError';

import { ROVO_AGENTS_FE_TEAM_NAME } from '../../../common/constants/private.ts';

import type { paginatedAgentsList_AtlassianConversationAssistantAgent_PaginatedAgentsListContent$key } from './__generated__/paginatedAgentsList_AtlassianConversationAssistantAgent_PaginatedAgentsListContent.graphql.ts';
import type { paginatedAgentsListPaginationQuery } from './__generated__/paginatedAgentsListPaginationQuery.graphql.ts';
import type {
	AgentStudioAgentQueryInput,
	paginatedAgentsListQuery,
} from './__generated__/paginatedAgentsListQuery.graphql.ts';
import { AgentListItem } from './list-item/index.tsx';
import { LoadingIndicator } from './loading-indicator/index.tsx';
import messages from './messages.ts';
import { MoreLessButton } from './more-less-button/index.tsx';
import { NoListItemsMessage } from './no-list-items-message/index.tsx';
import { Title } from './title/index.tsx';

type PaginatedAgentsListProps = {
	queryInput?: AgentStudioAgentQueryInput;
	sectionTitle?: string;
	onClickChat?: (agent: Agent) => void;
	onClickAgent?: (agent: Agent) => void;
} & AssistanceServiceInputParam;

const INITIAL_PAGE_SIZE = 5;
const LOAD_MORE_SIZE = 200;

const PaginatedAgentsListContent = ({
	queryInput,
	assistanceServiceParams,
	sectionTitle,
	onClickChat,
	onClickAgent,
}: PaginatedAgentsListProps) => {
	const { formatMessage } = useIntl();
	const [isExpanded, setIsExpanded] = useState(false);

	const preloadedRef = useLazyLoadQuery<paginatedAgentsListQuery>(
		graphql`
			query paginatedAgentsListQuery(
				$cloudId: String!
				$input: AgentStudioAgentQueryInput
				$product: String
				$first: Int
			) {
				...paginatedAgentsList_AtlassianConversationAssistantAgent_PaginatedAgentsListContent
					@arguments(cloudId: $cloudId, input: $input, product: $product, first: $first)
			}
		`,
		{
			cloudId: assistanceServiceParams.siteId,
			input: queryInput,
			product: assistanceServiceParams.product,
			first: INITIAL_PAGE_SIZE,
		},
		{
			networkCacheConfig: {
				metadata: {
					headers: assistanceServiceParams.headers,
				},
			},
		},
	);

	const { data, hasNext, isLoadingNext, loadNext } = usePaginationFragment<
		paginatedAgentsListPaginationQuery,
		paginatedAgentsList_AtlassianConversationAssistantAgent_PaginatedAgentsListContent$key
	>(
		graphql`
			fragment paginatedAgentsList_AtlassianConversationAssistantAgent_PaginatedAgentsListContent on Query
			@argumentDefinitions(
				first: { type: "Int", defaultValue: 5 }
				after: { type: "String" }
				cloudId: { type: "String!" }
				input: { type: "AgentStudioAgentQueryInput" }
				product: { type: "String" }
			)
			@refetchable(queryName: "paginatedAgentsListPaginationQuery") {
				agentStudio_getAgents(
					cloudId: $cloudId
					input: $input
					first: $first
					after: $after
					product: $product
				)
					@optIn(to: "AgentStudio")
					# This connection key is shared between the panel, fullscreen, modal and favourites in the modal - be careful when changing it
					@connection(key: "paginatedAgentsList_agentStudio_getAgents") {
					edges {
						node {
							...listItem_AtlassianConversationAssistantAgent_AgentListItem
						}
					}
				}
			}
		`,
		preloadedRef,
	);

	const allAgentNodes = useMemo(() => {
		const edges = data?.agentStudio_getAgents?.edges;
		if (!edges) {
			return [];
		}

		return edges.map((edge) => edge.node).filter((node) => node != null);
	}, [data]);

	const displayedAgentNodes = useMemo(() => {
		if (isExpanded) {
			return allAgentNodes;
		}
		return allAgentNodes.slice(0, INITIAL_PAGE_SIZE);
	}, [allAgentNodes, isExpanded]);

	const handleLoadMore = useCallback(() => {
		if (hasNext && !isLoadingNext) {
			loadNext(LOAD_MORE_SIZE);
		}
		setIsExpanded(true);
	}, [isLoadingNext, hasNext, loadNext]);

	const handleHide = useCallback(() => {
		setIsExpanded(false);
	}, []);

	// Show "Hide" button when expanded and there are more than INITIAL_PAGE_SIZE items
	const shouldShowHideButton = isExpanded && allAgentNodes.length > INITIAL_PAGE_SIZE;

	// Show "Load More" button when collapsed and either:
	// - There are more items to load from server (hasNext), OR
	// - We already have more items loaded locally than INITIAL_PAGE_SIZE
	const shouldShowLoadMoreButton =
		!isExpanded && (hasNext || allAgentNodes.length > INITIAL_PAGE_SIZE);

	if (allAgentNodes.length === 0) {
		return (
			<Stack space="space.050">
				{sectionTitle && <Title title={sectionTitle} />}

				<NoListItemsMessage text={formatMessage(messages.noListItemsMessage)} />
			</Stack>
		);
	}
	return (
		<Stack>
			{sectionTitle && <Title title={sectionTitle} />}
			<Stack space="space.050" testId="favourite-agents-list">
				{displayedAgentNodes.map((node, idx) => (
					<AgentListItem
						key={idx}
						node={node}
						onClickChat={onClickChat}
						onClickAgent={onClickAgent}
					/>
				))}
			</Stack>

			{isLoadingNext && <LoadingIndicator label={formatMessage(messages.loadingMoreAgents)} />}

			{shouldShowLoadMoreButton && (
				<MoreLessButton
					label={formatMessage(messages.loadMoreButton)}
					onClick={handleLoadMore}
					iconBefore={ChevronDownIcon}
				/>
			)}

			{shouldShowHideButton && (
				<MoreLessButton
					label={formatMessage(messages.hideButton)}
					onClick={handleHide}
					iconBefore={ChevronUpIcon}
				/>
			)}
		</Stack>
	);
};

export const PaginatedAgentsList = (props: PaginatedAgentsListProps): React.JSX.Element => {
	return (
		<ErrorBoundary
			fallbackRender={({ error }) => (
				<>
					<ExperienceFailure error={error} name={ExperienceName.AGENTS_MODAL_FAVOURITE_AGENTS} />
					<ChatGenericError errorType="generic" />
				</>
			)}
			onError={(error) => {
				logException({
					exception: error,
					name: ExperienceName.AGENTS_MODAL_FAVOURITE_AGENTS,
					tags: {
						siteId: props.assistanceServiceParams.siteId,
						teamName: ROVO_AGENTS_FE_TEAM_NAME,
						category: props.sectionTitle,
					},
				});
			}}
		>
			<Suspense
				fallback={
					<Stack space="space.100">
						<Skeleton height={20} width={100} />
						<Stack space="space.050">
							{Array.from({ length: INITIAL_PAGE_SIZE }).map((_, index) => (
								<Inline key={index} space="space.100" alignBlock="center">
									<Skeleton height={24} width={24} />
									<Skeleton height={16} width={120} />
								</Inline>
							))}
						</Stack>
					</Stack>
				}
			>
				<ExperienceStart name={ExperienceName.AGENTS_MODAL_FAVOURITE_AGENTS} />
				<PaginatedAgentsListContent {...props} />

				<ExperienceSuccess name={ExperienceName.AGENTS_MODAL_FAVOURITE_AGENTS} />
			</Suspense>
		</ErrorBoundary>
	);
};
